/**
 * @file Header.tsx
 * @description Top application header — fully responsive.
 *
 * Features:
 *  - Mobile hamburger (md:hidden)
 *  - Desktop sidebar collapse toggle (hidden md:flex)
 *  - Module search button → opens CommandSearch palette (Ctrl+K / ⌘K)
 *  - Theme toggle
 *  - Notification bell
 *  - User profile dropdown
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Search,
  User,
  LogOut,
  Settings,
  ChevronDown,
} from 'lucide-react';
import useUIStore from '@/store/uiStore';
import useAuthStore from '@/store/authStore';
import { ROUTES } from '@/utils/constants';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CommandSearch from './CommandSearch';

interface HeaderProps {
  onMobileMenuOpen: () => void;
}

const Header = ({ onMobileMenuOpen }: HeaderProps) => {
  const { isSidebarCollapsed, toggleSidebar, isDarkMode, toggleTheme } = useUIStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'A';

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <>
      {/* ── Command Search Palette ── */}
      <CommandSearch open={searchOpen} onOpenChange={setSearchOpen} />

      <header className="sticky top-0 z-30 h-16 flex items-center gap-2 px-3 sm:px-4 bg-header/90 backdrop-blur-md border-b border-border/60 shrink-0 shadow-sm">
        <button
          onClick={onMobileMenuOpen}
          className="md:hidden flex items-center justify-center p-2 rounded-lg text-foreground hover:text-primary transition-all active:scale-95 cursor-pointer shrink-0 group/hamburger"
          aria-label="Open menu"
        >
          <div className="flex flex-col gap-1.5 justify-center items-start w-5 h-4">
            <span className="h-0.5 w-5 bg-foreground rounded-full transition-all duration-300 group-hover/hamburger:w-4" />
            <span className="h-0.5 w-3 bg-gradient-to-r from-[#FFD941] via-[#FEC448] to-[#CC8400] rounded-full transition-all duration-300 group-hover/hamburger:w-5" />
            <span className="h-0.5 w-5 bg-foreground rounded-full transition-all duration-300 group-hover/hamburger:w-4" />
          </div>
        </button>

        {/* Desktop: sidebar collapse toggle */}
        <button
          onClick={toggleSidebar}
          className="hidden md:flex p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
          aria-label="Toggle sidebar"
        >
          {isSidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
        </button>

        {/* ── Search trigger ── */}
        {/* Wide screen: inline pill button */}
        <button
          onClick={() => setSearchOpen(true)}
          className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-xl border border-border bg-muted/50 text-muted-foreground hover:bg-accent hover:text-foreground hover:border-border/80 transition-all duration-200 text-sm flex-1 max-w-xs"
          aria-label="Search modules"
        >
          <Search size={14} className="shrink-0" />
          <span className="flex-1 text-left">Search modules…</span>
          <kbd className="hidden lg:flex h-5 items-center gap-0.5 rounded border border-border bg-background px-1.5 font-mono text-[10px] shrink-0">
            Ctrl K
          </kbd>
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* ── Right side controls ── */}
        <div className="flex items-center gap-1">
          {/* Mobile: search icon only */}
          <button
            onClick={() => setSearchOpen(true)}
            className="sm:hidden p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Search"
          >
            <Search size={18} />
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Toggle theme"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Notifications */}
          {/* <button
            className="relative p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Notifications"
          >
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary border-2 border-background" />
          </button> */}

          {/* Divider */}
          <div className="w-px h-6 bg-border mx-1 shrink-0" />

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-xl px-2 py-1 hover:bg-accent transition-colors group max-w-[180px]">
                <Avatar className="w-8 h-8 shrink-0 ring-2 ring-primary/30">
                  <AvatarFallback className="text-xs font-bold bg-primary text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col items-start min-w-0">
                  <span className="text-sm font-semibold text-foreground leading-tight truncate max-w-[100px]">
                    {user?.firstName ?? 'Admin'}
                  </span>
                  <span className="text-[10px] text-muted-foreground leading-tight">
                    Administrator
                  </span>
                </div>
                <ChevronDown
                  size={14}
                  className="hidden md:block text-muted-foreground group-hover:text-foreground transition-colors shrink-0"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-1">
                  <p className="font-semibold text-sm text-foreground">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={ROUTES.PROFILE} className="cursor-pointer">
                  <User size={15} className="mr-2 text-muted-foreground" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings size={15} className="mr-2 text-muted-foreground" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/20 cursor-pointer"
              >
                <LogOut size={15} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
};

export default Header;
